import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function Privacy() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#a0a0a0]">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-[#a0a0a0] hover:text-[#c0c0c0] mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Paper List
        </Link>

        <div className="prose prose-invert">
          <h1>Privacy Policy for Gmail to Jira Assistant</h1>
          <p className="text-[#808080]">Last updated: March 2024</p>

          <h2>Overview</h2>
          <p>
            Gmail to Jira Assistant is a browser extension that helps you create Jira tasks from Gmail emails. 
            We take your privacy seriously and are committed to protecting your data.
          </p>

          <h2>Data Collection and Usage</h2>
          <p>We collect and process the following data:</p>

          <h3>Required Information</h3>
          <ul>
            <li>Name (for AI context)</li>
            <li>Email address (for Jira authentication)</li>
            <li>Company name (for AI context)</li>
            <li>Jira API token (for Jira integration)</li>
            <li>OpenAI API key (for AI features)</li>
          </ul>

          <h3>Email Content</h3>
          <ul>
            <li>Email subjects and bodies are temporarily processed to generate task summaries</li>
            <li>No email content is stored permanently</li>
            <li>Email data is only sent to:
              <ul>
                <li>Your Jira instance (for task creation)</li>
                <li>OpenAI API (for summarization)</li>
                <li>do.ne.at (for Paper List integration)</li>
              </ul>
            </li>
          </ul>

          <h2>Data Storage</h2>
          <ul>
            <li>All configuration data is stored locally in your browser using <code>chrome.storage.local</code></li>
            <li>No data is stored on our servers</li>
            <li>API keys and tokens are stored securely in your browser</li>
          </ul>

          <h2>Data Transmission</h2>
          <ul>
            <li>All data transmission uses secure HTTPS connections</li>
            <li>We implement strict Content Security Policy (CSP) rules</li>
            <li>API calls are limited to specific endpoints:
              <ul>
                <li><code>*.atlassian.net/rest/api/2/*</code></li>
                <li><code>api.openai.com/v1/chat/completions</code></li>
                <li><code>do.ne.at/*</code></li>
              </ul>
            </li>
          </ul>

          <h2>Third-Party Services</h2>
          <p>We integrate with:</p>
          <ol>
            <li>
              <strong>Jira (Atlassian)</strong>
              <ul>
                <li>For task creation</li>
                <li>Subject to Atlassian's privacy policy</li>
              </ul>
            </li>
            <li>
              <strong>OpenAI</strong>
              <ul>
                <li>For email summarization</li>
                <li>Subject to OpenAI's privacy policy</li>
              </ul>
            </li>
            <li>
              <strong>Paper List (do.ne.at)</strong>
              <ul>
                <li>For task list integration</li>
                <li>Subject to Paper List's privacy policy</li>
              </ul>
            </li>
          </ol>

          <h2>Data Protection</h2>
          <ul>
            <li>No analytics or tracking</li>
            <li>No data sharing with third parties</li>
            <li>No data persistence beyond configuration</li>
            <li>No cookies used</li>
          </ul>

          <h2>Your Rights</h2>
          <p>You can:</p>
          <ul>
            <li>Access your stored data through the extension options</li>
            <li>Delete your data by removing the extension</li>
            <li>Update your information at any time</li>
          </ul>

          <h2>Changes to This Policy</h2>
          <p>
            We may update this privacy policy. Any changes will be reflected in the "Last updated" date.
          </p>

          <h2>Contact</h2>
          <p>
            For privacy concerns, contact:{' '}
            <a 
              href="mailto:gary@shedcollective.org"
              className="text-[#c0c0c0] hover:text-white"
            >
              gary@shedcollective.org
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}